/**
 * BotIntelligencePanel.jsx
 * BerBul AI Trading · Bot Intelligence Panel v2.0 (Production)
 *
 * Placement: TradingCommand.js — between MarketChart and EvaluationTimeline
 *
 * Props contract (Section 11.1 of BRD):
 *   liveBars          - Full bar array (primary data source)
 *   activePositions   - Derived from positions with open_qty !== 0
 *   closedPositions   - Derived from positions with open_qty === 0
 *   currentPrice      - Live price tick
 *   proceedEvals      - Optional: bars filtered for PROCEED/BUY evals (options matching)
 *   allOrders         - Optional: raw orders array
 *   metrics           - { winRate, closedTrades, ... } from TradingCommand metrics memo
 *   onClosePosition   - Callback for manual override close
 *
 * Usage in TradingCommand.js:
 *   import BotIntelligencePanel from "./components/TradingCommand/BotIntelligencePanel";
 *   ...
 *   <BotIntelligencePanel
 *     liveBars={liveBars}
 *     activePositions={activePositions}
 *     closedPositions={closedPositions}
 *     currentPrice={currentPrice}
 *     metrics={metrics}
 *     onClosePosition={handleClosePosition}
 *   />
 */

import { useState, useEffect, useRef, useMemo } from "react";

// ─── FONTS ────────────────────────────────────────────────────────────────────
const FONTS = `@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Syne:wght@600;700;800&display=swap');`;

// ─── THEME ────────────────────────────────────────────────────────────────────
// BRD v1.0 Section 6.1 — Design System Reference (homepage palette)
const C = {
  bg:      "#08101E",   // Page background (BRD: bg)
  surface: "#0C1525",   // Card / surface background (BRD: bg1)
  s2:      "#101B2E",   // Hover state background (BRD: bg2)
  s3:      "#0e1828",   // Slightly elevated surface (between bg1 and bg2)
  border:  "#1E2D42",   // Card borders (BRD: border)
  b2:      "#2a3d56",   // Secondary / inner borders
  text:    "#E8EDF5",   // Primary text — headlines (BRD: text)
  muted:   "#6A7D95",   // Labels, captions, eyebrows (BRD: meta)
  subtle:  "#1E2D42",   // Dim backgrounds (reuse border token)
  body:    "#B8C8DC",   // Body text — descriptions (BRD: body)
  green:   "#00E896",   // Win, positive, TRENDING (BRD: green)
  cyan:    "#00C8FF",   // Confidence, primary accent, SELECTIVE (BRD: cyan)
  amber:   "#FFB340",   // Warning, cautious (BRD: amber)
  red:     "#FF4466",   // Loss, stop, risk, DEFENSIVE (BRD: red)
  blue:    "#4488FF",   // PREDICT layer, models (BRD: blue)
  purple:  "#9966FF",   // Patterns, RECOVERY, LEARN layer (BRD: purple)
  gDim:    "rgba(0,232,150,0.10)",
  cDim:    "rgba(0,200,255,0.10)",
  aDim:    "rgba(255,179,64,0.10)",
  rDim:    "rgba(255,68,102,0.10)",
  bDim:    "rgba(68,136,255,0.10)",
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const pct    = (v) => `${Math.round((v || 0) * 100)}%`;
const fmt2   = (v) => typeof v === "number" ? v.toFixed(2) : "—";
const rfCount = (rf) => rf ? Object.values(rf).filter(Boolean).length : 0;

const confColor = (b) =>
  ({ VERY_LOW: C.red, LOW: C.amber, MEDIUM: C.cyan, HIGH: C.green, VERY_HIGH: C.green })[b] || C.muted;

const bucketFromScore = (score) => {
  if (score == null) return null;
  if (score >= 0.80) return "VERY_HIGH";
  if (score >= 0.65) return "HIGH";
  if (score >= 0.45) return "MEDIUM";
  if (score >= 0.25) return "LOW";
  return "VERY_LOW";
};

const tagColor = (v) =>
  v === "against" ? C.red : v === "neutral" ? C.muted : C.green;

// Normalize tag values: "against_macro" → "against", "aligned" → "aligned", null → "neutral"
const normTag = (v) => {
  if (!v) return "neutral";
  if (v.startsWith("against")) return "against";
  if (v.startsWith("aligned") || v.startsWith("with") || v.startsWith("bullish")) return "aligned";
  return "neutral";
};

// ─── AGENT STORY EXTRACTOR ───────────────────────────────────────────────────
// Handles both new and old schemas (BRD Section 11.3)
function extractAgentStory(bar) {
  if (!bar) return null;
  const story = bar.eval_agent_story;
  if (!story) return null;

  // Chart analysis (world model data)
  const chartAnalysis = story.chart_analysis || {};
  const llmFull = chartAnalysis.llm_full_response || chartAnalysis.llm_response || {};
  const regimeAnalysis = llmFull.regime_analysis || {};
  const overallAssessment = llmFull.overall_assessment || llmFull.market_assessment || {};
  const fftLag = llmFull.fft_lag_intelligence || {};

  // Execution path (new schema first → old fallback)
  const fastPathAnalysis = story.fast_path_analysis || {};
  const execPath = fastPathAnalysis.path_taken ||
    story.journey?.path ||
    (fastPathAnalysis.fast_path_taken ? "fast_path" : "full_path");

  // Decision block (new schema: story.decision)
  const ds = story.decision || {};

  // Decision: new schema uses story.decision.final, old uses bar.eval_decision
  const decision = ds.final || story.eval_decision || bar.eval_decision || bar.decision || "SKIP";

  // Confidence: new schema uses story.decision.confidence (numeric 0-1)
  // perf.confidence_level is sometimes a label string like "MEDIUM" — skip if non-numeric
  const confidenceRaw = ds.confidence ??
    story.confidence_score ??
    bar.eval_confidence ??
    null;
  const confidence = typeof confidenceRaw === "number" ? confidenceRaw : null;

  // confidence_bucket: derive from numeric score, or fall back to perf string label
  const perfConfLevel = story.performance?.confidence_level;
  const confBucket = confidence != null
    ? bucketFromScore(confidence)
    : (typeof perfConfLevel === "string" ? perfConfLevel : null);

  // CI from decision block
  const ciArr = Array.isArray(ds.confidence_ci) && ds.confidence_ci.length === 2
    ? ds.confidence_ci : null;

  // Thinking arc (new schema: thinking_snapshots → old: thinking.decision_evolution)
  let thinking = null;
  const ts = story.thinking_snapshots;
  if (ts) {
    const initial = ts.initial_thinking || {};
    const combined = ts.combined_thinking || {};
    const final = ts.final_thinking || {};
    thinking = {
      initial:  { decision: initial.provisional_decision || "—", conf: initial.confidence ?? null },
      combined: { rec: combined.consistency_recommendation || combined.decision || "—" },
      final:    {
        decision:      final.decision || decision,
        conf:          final.confidence ?? confidence,
        override:      !!(final.trigger_reason || final.override),
        overrideReason: final.trigger_reason || final.override_reason || null,
      },
    };
  } else if (story.thinking?.decision_evolution) {
    const de = story.thinking.decision_evolution;
    thinking = {
      initial:  { decision: de.initial?.decision || "—", conf: de.initial?.confidence ?? null },
      combined: { rec: de.combined?.recommendation || "—" },
      final:    {
        decision:      de.final?.decision || decision,
        conf:          de.final?.confidence ?? confidence,
        override:      !!(de.final?.override),
        overrideReason: de.final?.trigger_reason || null,
      },
    };
  }

  // Rule votes (new: thinking_snapshots.initial_thinking.rule_votes → old: rule_votes)
  const ruleVotes = story.thinking_snapshots?.initial_thinking?.rule_votes ||
    story.rule_votes || null;

  // Evidence weights
  const evidenceWeights = story.thinking_snapshots?.initial_thinking?.weights ||
    story.weights || null;

  // Timeframe protection
  const tfp = story.timeframe_protection || {};
  const timeframeLock = {
    active: !!(tfp.active || tfp.timeframe_lock_active),
    remainingMin: tfp.remaining_min ?? tfp.remaining_minutes ?? 0,
  };

  // Performance
  const perf = story.performance || {};
  const performance = {
    successRate:  perf.success_rate ?? null,
    targetHit:    perf.target_hit_rate ?? null,
    stopHit:      perf.stop_hit_rate ?? null,
    avgPnl:       perf.avg_pnl ?? null,
    ev:           perf.expected_value ?? null,
    supports:     perf.supports_decision ?? true,
    similarity:   perf.similarity ?? null,
    cases:        perf.total_cases ?? null,
    stmRate:      perf.stm_success_rate ?? null,
    ltmRate:      perf.ltm_success_rate ?? null,
  };

  // Memory (new: memory_summary → old: memory)
  const memorySrc = story.memory_summary || story.memory || {};
  const memory = {
    cases:      memorySrc.found_cases ?? memorySrc.cases ?? 0,
    recent:     memorySrc.recent_cases ?? memorySrc.recent ?? 0,
    historical: memorySrc.historical_cases ?? memorySrc.historical ?? 0,
    similarity: memorySrc.top_similarity ?? memorySrc.similarity ?? 0,
    note:       memorySrc.summary_note ?? memorySrc.note ?? null,
  };

  // Patterns
  // pattern_tag_analysis is an OBJECT (single pattern), not array
  // identified_patterns inside llm_full_response is the array
  const ptag = story.pattern_tag_analysis && typeof story.pattern_tag_analysis === "object"
    ? story.pattern_tag_analysis : null;
  const llmPatterns = Array.isArray(llmFull.identified_patterns) ? llmFull.identified_patterns : [];
  const legacyPatterns = Array.isArray(story.patterns) ? story.patterns : [];

  // Build unified patterns array from best available source
  const patterns = llmPatterns.length > 0
    ? llmPatterns.map((p) => ({
        name:        p.pattern_name || p.name || "Unknown",
        conf:        p.confidence ?? 0,
        formed:      p.formation_completeness ?? 0.8,
        volConf:     p.volume_confirmed ?? false,
        reliability: p.historical_reliability ?? 0,
        tags: {
          // trend_tags uses "against_macro" / "against_micro" / "against_position" format
          macro:    normTag(p.trend_tags?.vs_macro    ?? p.tags?.macro_alignment    ?? p.tags?.macro),
          micro:    normTag(p.trend_tags?.vs_micro    ?? p.tags?.micro_alignment    ?? p.tags?.micro),
          position: normTag(p.trend_tags?.vs_position ?? p.tags?.position_alignment ?? p.tags?.position),
        },
      }))
    : legacyPatterns.map((p) => ({
        name:        p.pattern || p.name || "Unknown",
        conf:        p.confidence ?? p.conf ?? 0,
        formed:      p.formation_completeness ?? p.formed ?? 0,
        volConf:     p.volume_confirmed ?? p.volConf ?? false,
        reliability: p.historical_reliability ?? p.reliability ?? 0,
        tags: {
          macro:    p.tags?.macro_alignment ?? p.tags?.macro ?? "neutral",
          micro:    p.tags?.micro_alignment ?? p.tags?.micro ?? "neutral",
          position: p.tags?.position_alignment ?? p.tags?.position ?? "neutral",
        },
      }));

  // If only ptag (single pattern object) and no array, add it
  if (patterns.length === 0 && ptag && ptag.pattern) {
    patterns.push({
      name:    ptag.pattern,
      conf:    ptag.confidence ?? 0,
      formed:  0.8,
      volConf: false,
      reliability: 0,
      tags: {
        macro:    ptag.direction === "BEARISH" ? "against" : ptag.direction === "BULLISH" ? "aligned" : "neutral",
        micro:    "neutral",
        position: "neutral",
      },
    });
  }

  // Pattern rule
  const patternRule = story.pattern_decision_rule ||
    ptag?.action_map_rule ||
    story.patternRule || null;

  // Risk flags
  // New schema: story.chart_analysis.llm_full_response.risk_factors (with _high/_risk suffixes)
  // Also: story.risk_controls, story.red_flags (older schemas)
  const rc  = story.risk_controls || {};
  const rf  = story.red_flags || {};
  const rfa = llmFull.risk_factors || {};  // from chart_analysis
  const riskFlags = {
    reversalRisk:      !!(rfa.reversal_risk_high    || rc.reversal_risk      || rf.reversal_risk      || false),
    breakdownRisk:     !!(rfa.pattern_breakdown_risk|| rc.breakdown_risk     || rf.breakdown_risk     || false),
    falseBreakout:     !!(rfa.false_breakout_risk   || rc.false_breakout     || rf.false_breakout     || false),
    modelDisagreement: !!(rfa.model_disagreement_risk||rc.model_disagreement || rf.model_disagreement || false),
    lagTiming:         !!(rfa.lag_timing_risk       || rc.lag_timing         || rf.lag_timing         || false),
  };

  // Market context
  const support      = overallAssessment.key_support_level ?? overallAssessment.support_level ?? null;
  const resistance   = overallAssessment.key_resistance_level ?? overallAssessment.resistance_level ?? null;
  const regime       = regimeAnalysis.regime_classification || null;
  const flipRate     = regimeAnalysis.flip_rate_assessment ?? null;
  const integratedAnalysis = llmFull.integrated_analysis || {};
  const timingSync   = !!(integratedAnalysis.timing_convergence ?? fftLag.timing_convergence ?? fftLag.timing_sync ?? false);

  // Model intelligence — new schema has it in llmFull.model_intelligence
  const mi = llmFull.model_intelligence || {};

  // DRL direction: from model_intelligence first, then bar.action_drl (0=SELL,1=HOLD,2=BUY)
  const drlDir = mi.drl_trend_direction;  // "bullish"/"bearish"/"neutral"
  const drlRaw = bar.action_drl;
  const modelDRL = drlDir
    ? drlDir
    : (drlRaw === 2 ? "bullish" : drlRaw === 0 ? "bearish" : "neutral");

  // DT direction
  const dtDir = mi.dt_trend_direction;
  const trend = bar.trend || "";
  const modelDT = dtDir
    ? dtDir
    : (trend === "BUY" ? "bullish" : trend === "SELL" ? "bearish" : "neutral");

  // Model leadership
  const modelLeadership = mi.model_leadership_pattern ||
    story.contradictions?.model_vs_pattern ||
    (modelDRL === modelDT ? "aligned" : "chaotic");

  // Consensus strength
  const consensusStrength = mi.model_consensus_strength ?? null;

  // Confidence interval
  const ciLow  = confidence != null ? Math.max(0, confidence - 0.10) : null;
  const ciHigh = confidence != null ? Math.min(1, confidence + 0.10) : null;

  // Options
  const optionStrategy = llmFull.option_strategy || llmFull.options_strategy || null;
  const optionsNotes   = llmFull.options_notes || null;
  const primarySignal  = story.primary_signal || story.primarySignal || "unknown";

  // Flags (warning tags)
  const flagsRaw = story.evaluation_flags || story.flags || [];
  const flags = Array.isArray(flagsRaw) ? flagsRaw : [];

  return {
    decision, confidence, confBucket, ciArr,
    path: execPath, thinking, ruleVotes, evidenceWeights,
    timeframeLock, performance, memory, patterns,
    patternRule, riskFlags, primarySignal, flags,
    optionStrategy, optionsNotes,
    marketContext: { support, resistance, regime, flipRate, timingSync, modelDRL, modelDT, modelLeadership },
    evalType: bar.eval_type || story.eval_type || "PROACTIVE",
    positionId: bar.eval_position_id || bar.position_id || null,
    timestamp: bar.t || bar.timestamp || null,
  };
}

// ─── DATA EXTRACTOR ───────────────────────────────────────────────────────────
// Derives everything the panel needs from raw TradingCommand props
function usePanelData({ liveBars, activePositions, closedPositions, currentPrice, proceedEvals, metrics }) {

  return useMemo(() => {
    // ── Panel state ──────────────────────────────────────────────────────────
    const activePos = activePositions?.[0] || null;
    const lastClosed = closedPositions?.length > 0
      ? closedPositions[closedPositions.length - 1]
      : null;

    let panelState = "watching";
    if (activePos) panelState = "active";
    else if (lastClosed && lastClosed.exitTime) {
      const closedAgo = (Date.now() - new Date(lastClosed.exitTime)) / 60000;
      if (closedAgo < 30) panelState = "post_trade";
    }

    // ── Today stats ──────────────────────────────────────────────────────────
    const today = new Date().toDateString();
    let evaluated = 0, proceeded = 0, skipped = 0;
    (liveBars || []).forEach((bar) => {
      const barDate = bar.t ? new Date(bar.t).toDateString() : "";
      if (barDate !== today) return;
      const dec = bar.eval_decision || bar.decision;
      if (!dec) return;
      evaluated++;
      if (dec === "PROCEED" || dec === "BUY") proceeded++;
      else if (dec === "SKIP_TRADE")                skipped++;
    });
    const todayStats = { evaluated, proceeded, skipped, active: activePositions?.length || 0 };

    // ── Last bar with chart_analysis + support/resistance levels ──────────────
    // Any bar (proactive or reactive) can have chart_analysis in new schema.
    // We want the most recent bar whose llm_full_response.overall_assessment
    // has key_support_level — that's where world model levels live.
    const barsWithLevels = (liveBars || []).filter((b) => {
      const oa = b.eval_agent_story
        ?.chart_analysis
        ?.llm_full_response
        ?.overall_assessment;
      return oa?.key_support_level != null || oa?.key_resistance_level != null;
    });
    const lastProactiveBar = barsWithLevels.length > 0
      ? barsWithLevels[barsWithLevels.length - 1]
      : (liveBars?.length > 0 ? liveBars[liveBars.length - 1] : null);

    // ── Last reactive bar (for active position) ──────────────────────────────
    const positionId = activePos?.id || activePos?.a_id;
    const reactiveBars = positionId
      ? (liveBars || []).filter((b) => b.eval_position_id === positionId || b.position_id === positionId)
      : [];
    const lastReactiveBar = reactiveBars.length > 0 ? reactiveBars[reactiveBars.length - 1] : null;

    // ── Reactive history (last 6 for trend strip) ────────────────────────────
    const reactiveHistory = reactiveBars.slice(-6).map((b) => {
      const story = b.eval_agent_story;
      const conf = story?.performance?.confidence_level ?? b.eval_confidence ?? 0;
      const ev   = story?.performance?.expected_value ?? null;
      const rf   = story?.risk_controls || story?.red_flags || {};
      const flagCount = Object.values(rf).filter(Boolean).length;
      const dec  = b.eval_decision || b.decision || "—";
      return {
        time:     b.t ? new Date(b.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) : "—",
        conf,
        ev,
        flags:    flagCount,
        decision: dec,
      };
    });

    // ── Last PROCEED bar — richest agent story (has thinking, votes, etc.) ─────
    const lastProceedBar = (liveBars || []).slice().reverse().find((b) =>
      (b.eval_decision === "PROCEED" || b.eval_decision === "BUY") && b.eval_agent_story
    ) || null;

    // ── Agent story selection ────────────────────────────────────────────────
    // Active: use reactive (position-specific) or last proceed for full story
    // Watching/post-trade: use last proceed bar for richest analysis data
    const agentBar = panelState === "active"
      ? (lastReactiveBar || lastProceedBar || lastProactiveBar)
      : (lastProceedBar || lastProactiveBar);
    const agentStory = extractAgentStory(agentBar);

    // Market context levels always from bar with chart_analysis + levels
    const proactiveStory = extractAgentStory(lastProactiveBar);

    // ── Market context ───────────────────────────────────────────────────────
    // Prefer proactive for levels, reactive for regime/model state if active
    const ctx = agentStory?.marketContext || {};
    const proCtx = proactiveStory?.marketContext || {};
    const marketContext = {
      regime:      ctx.regime || proCtx.regime || null,
      flipRate:    ctx.flipRate ?? proCtx.flipRate ?? 0,
      volatility:  "—",
      // support/resistance always from proactive bar (chart_analysis)
      support:     proCtx.support ?? ctx.support ?? null,
      resistance:  proCtx.resistance ?? ctx.resistance ?? null,
      currentPrice,
      // only show entry/target/stop when actively in a position — clears immediately on close
      entryPrice:  (panelState === "active" && activePos?.entryPrice  > 0 ? activePos.entryPrice  : null),
      targetPrice: (panelState === "active" && (activePos?.target > 0 ? activePos.target : activePos?.tp  > 0 ? activePos.tp  : null)),
      stopPrice:   (panelState === "active" && (activePos?.stop   > 0 ? activePos.stop   : activePos?.sl  > 0 ? activePos.sl  : null)),
      modelDRL:    ctx.modelDRL || proCtx.modelDRL || "neutral",
      modelDT:     ctx.modelDT  || proCtx.modelDT  || "neutral",
      modelLeadership: ctx.modelLeadership || proCtx.modelLeadership || "unknown",
      timingSync:  ctx.timingSync ?? proCtx.timingSync ?? false,
    };

    // ── Stock info ───────────────────────────────────────────────────────────
    let stock = null;
    if (activePos && panelState === "active") {
      const pnl = activePos.unrealizedPL || 0;
      stock = {
        side:    activePos.isLong ? "LONG" : "SHORT",
        qty:     activePos.qty || activePos.open_qty || 0,
        entry:   activePos.entryPrice,
        current: currentPrice,
        target:  activePos.target || activePos.tp || 0,
        stop:    activePos.stop   || activePos.sl  || 0,
        pnl,
        pnlPct:  activePos.entryPrice > 0 ? pnl / (activePos.entryPrice * (activePos.qty || 1)) : 0,
      };
    } else if (lastClosed && panelState === "post_trade") {
      stock = {
        side:    lastClosed.isLong ? "LONG" : "SHORT",
        qty:     lastClosed.qty || 0,
        entry:   lastClosed.entryPrice,
        exit:    lastClosed.exitPrice,
        target:  lastClosed.target || lastClosed.tp || 0,
        stop:    lastClosed.stop   || lastClosed.sl  || 0,
        pnl:     lastClosed.unrealizedPL || 0,
        pnlPct:  0,
        closed:  true,
      };
    }

    // ── Options info (from matched proceedEval or agentStory) ────────────────
    let options = null;
    const optionStrategy = agentStory?.optionStrategy;
    if (optionStrategy) {
      if (panelState === "active") {
        options = {
          strategy:       optionStrategy,
          status:         "ACTIVE",
          note:           agentStory.optionsNotes || "Closes when stock hits target or stop",
          estimatedValue: null,
        };
      } else if (panelState === "post_trade" && lastClosed) {
        options = {
          strategy:  optionStrategy,
          status:    "CLOSED",
          pnl:       null,
          closedBy:  lastClosed.isWin ? "stock target hit" : "stop loss triggered",
        };
      }
    }

    // ── Eval object ──────────────────────────────────────────────────────────
    // Normalized for both DashboardView and AnalysisView
    const ev = agentStory ? {
      type:          agentStory.evalType,
      path:          agentStory.path,
      decision:      agentStory.decision,
      confidence:    agentStory.confidence,           // null if not numeric
      bucket:        agentStory.confBucket || null,   // string label or null
      ci:            agentStory.ciArr ?? (
        agentStory.confidence != null ? [
          Math.max(0, agentStory.confidence - 0.10),
          Math.min(1, agentStory.confidence + 0.10),
        ] : null
      ),
      thinking:      agentStory.thinking,
      ruleVotes:     agentStory.ruleVotes,
      evidenceWeights: agentStory.evidenceWeights,
      timeframeLock: agentStory.timeframeLock,
      memory:        agentStory.memory,
      performance:   agentStory.performance,
      patterns:      agentStory.patterns,
      patternRule:   agentStory.patternRule,
      riskFlags:     agentStory.riskFlags,
      primarySignal: agentStory.primarySignal,
      flags:         agentStory.flags,
    } : null;

    // ── Orb color / pulse ────────────────────────────────────────────────────
    let orbColor = C.blue;
    let orbPulse = "slow";

    if (panelState === "active") {
      const evNeg = ev?.performance?.ev != null && ev.performance.ev < 0;
      const notSupported = ev?.performance?.supports === false;
      orbColor = (evNeg || notSupported) ? C.amber : C.green;
      orbPulse = "medium";
    } else if (panelState === "post_trade") {
      orbColor = lastClosed?.isWin ? C.green : C.red;
      orbPulse = "none";
    }

    // ── Headline (deterministic rule-based, BRD Section 5.3) ─────────────────
    let headline    = "Bot is scanning for setups";
    let subheadline = `${todayStats.evaluated} evaluated · ${todayStats.proceeded} proceeded · ${todayStats.skipped} skipped`;
    let tone        = "quiet";

    if (panelState === "watching") {
      const regime   = marketContext.regime;
      const flipRate = marketContext.flipRate;
      const hasEvals = todayStats.evaluated > 0;

      if (flipRate > 0.30 || regime === "SIDEWAYS" || regime === "CHOPPY") {
        headline = "Selective day — bot is waiting for cleaner conditions";
        tone     = "quiet";
      } else if (!hasEvals) {
        headline = "Market conditions below threshold — regime instability blocking setups";
        tone     = "quiet";
      } else if (regime === "WEAK_TREND" || regime === "TRENDING") {
        headline = "Trending conditions — Agent actively seeking setups";
        tone     = "confident";
      } else {
        headline = "Scanning — conditions evaluated, waiting for high-quality entry";
        tone     = "quiet";
      }

    } else if (panelState === "active") {
      const evVal      = ev?.performance?.ev ?? null;
      const supported  = ev?.performance?.supports ?? true;
      const confScore  = ev?.confidence ?? 0;
      const locked     = ev?.timeframeLock?.active;
      const memCases   = ev?.memory?.cases ?? 0;
      const memRate    = ev?.performance?.successRate ?? null;

      if (evVal != null && evVal < 0 && !supported) {
        headline = "Holding by discipline — conditions mixed but rules say stay";
        tone     = "disciplined";
        if (locked) {
          subheadline = `Timeframe lock active · EV slightly negative · holding by rules`;
        } else if (memCases > 0 && memRate != null) {
          subheadline = `${memCases} similar cases in memory · ${Math.round(memRate * 100)}% won · history mixed`;
        }
      } else if (confScore >= 0.70 && supported) {
        headline = "Holding as planned — conditions support continuation";
        tone     = "confident";
        if (memCases > 0 && memRate != null) {
          const targetDelta = stock?.target && stock?.current
            ? `$${Math.abs(stock.target - stock.current).toFixed(2)} to target`
            : null;
          subheadline = `${memCases} similar cases · ${Math.round(memRate * 100)}% won${targetDelta ? ` · ${targetDelta}` : ""}`;
        }
      } else if (locked) {
        headline = "Timeframe lock active — letting the trade develop";
        tone     = "disciplined";
        const remainMin = ev?.timeframeLock?.remainingMin ?? 0;
        subheadline = `Lock expires in ${remainMin}m · next reactive check on unlock`;
      } else {
        headline = "Monitoring position — bot running analysis every 5 minutes";
        tone     = "disciplined";
      }

    } else if (panelState === "post_trade") {
      const isWin      = lastClosed?.isWin;
      const holdMin    = lastClosed?.holdTimeMin ?? 0;
      const pnl        = lastClosed?.unrealizedPL ?? 0;
      const exitT      = lastClosed?.exitType || "";

      if (isWin) {
        headline    = "Target reached — trade closed as planned";
        subheadline = `Hold time: ${holdMin}m · Stock ${pnl >= 0 ? "+" : ""}$${Math.abs(pnl).toFixed(2)} · Resuming scan`;
        tone        = "confident";
      } else {
        headline    = "Stop triggered — loss contained, bot resuming scan";
        subheadline = `Hold time: ${holdMin}m · Stock $${pnl.toFixed(2)} · Exit: ${exitT}`;
        tone        = "quiet";
      }
    }

    return {
      panelState, orbColor, orbPulse, tone,
      headline, subheadline,
      todayStats, marketContext,
      stock, options, ev,
      reactiveHistory,
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveBars, activePositions, closedPositions, currentPrice, metrics]);
}

// ─── TOOLTIP ──────────────────────────────────────────────────────────────────
const TIPS = {
  "Flip Rate":        "How often the market regime switches between trending and sideways. High flip rate = unstable conditions where signals are less reliable.",
  "Timeframe Lock":   "Bot's own rule preventing early exit. Gives the trade time to develop before reassessing — overrides all non-emergency hold decisions.",
  "EV":               "Expected Value — average profit from similar historical trades. Negative EV means history slightly favors this not working, but bot is holding by its discipline rules.",
  "Vote Split":       "Internal vote of the bot's analysis engines before the final decision. HOLD 50% means the call was divided — not high conviction.",
  "Evidence Weights": "What types of analysis drove this decision. Patterns 55% means candlestick patterns were the biggest factor in this particular call.",
  "STM vs LTM":       "Short-Term Memory (recent trades) vs Long-Term Memory (historical). Stable = consistent edge. Diverging = recent market differs from history.",
  "Formation":        "How completely the candlestick pattern has formed. Below 70% means it's partially formed and significantly less reliable.",
  "Vol Confirmed":    "Whether trading volume confirmed the pattern. Patterns without volume confirmation are substantially less reliable signals.",
  "Fast Path":        "Bot reached such high conviction in stage 1 that it skipped the remaining 3 analysis stages. Positive signal — highly decisive.",
  "Full Path":        "Bot ran all 4 analysis stages — chart, initial reasoning, combined integration, final constraints. Thorough deliberation.",
  "Consensus":        "Agreement between the DRL (deep reinforcement learning) and DT (decision tree) models. Low consensus = conflicting model signals.",
  "Timing Sync":      "Whether all technical signals are firing at the same time. Unsynced means signals are staggered — less reliable for immediate entry.",
  "DEBIT_SPREAD":     "Options strategy that profits from the stock moving toward target. Limited risk, leveraged reward vs holding stock alone.",
  "Reward Bucket":    "Quality of the risk/reward opportunity at entry — independent of directional confidence. HIGH means good potential payoff even if confidence is moderate.",
};

function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  const [pos, setPos]   = useState({ x: 0, y: 0 });
  const timer = useRef(null);
  const tip = TIPS[text];
  if (!tip) return children;
  return (
    <span
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={(e) => {
        const x = e.clientX;
        const y = e.clientY;
        timer.current = setTimeout(() => { setPos({ x, y }); setShow(true); }, 300);
      }}
      onMouseLeave={() => { clearTimeout(timer.current); setShow(false); }}
    >
      {children}
      <span style={{
        position: "fixed", pointerEvents: "none", zIndex: 9999,
        left: pos.x + 12, top: pos.y - 8,
        opacity: show ? 1 : 0, transition: "opacity 0.15s",
        maxWidth: "260px", background: C.s3, border: `1px solid ${C.b2}`,
        borderRadius: "8px", padding: "8px 12px", fontSize: "11px",
        color: C.text, lineHeight: 1.6, boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
      }}>
        <span style={{ fontWeight: 700, color: C.cyan, fontSize: "10px", letterSpacing: "0.5px", fontFamily: "'DM Mono', monospace" }}>{text}</span>
        <br />{tip}
      </span>
    </span>
  );
}

// ─── STATUS ORB ───────────────────────────────────────────────────────────────
function StatusOrb({ color, pulse, size = 48 }) {
  const anim = pulse === "slow"
    ? "bipOrbSlow 3s ease-in-out infinite"
    : pulse === "medium"
    ? "bipOrbMed 2s ease-in-out infinite"
    : "none";
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <style>{`
        @keyframes bipOrbSlow { 0%,100%{box-shadow:0 0 0 0 ${color}40} 50%{box-shadow:0 0 0 ${size*0.4}px ${color}00} }
        @keyframes bipOrbMed  { 0%,100%{box-shadow:0 0 0 0 ${color}50} 50%{box-shadow:0 0 0 ${size*0.5}px ${color}00} }
      `}</style>
      <div style={{
        width: size, height: size, borderRadius: "50%",
        background: `radial-gradient(circle at 35% 35%, ${color}cc, ${color}66)`,
        border: `2px solid ${color}`,
        animation: anim,
        boxShadow: `0 0 20px ${color}40`,
      }} />
      <div style={{
        position: "absolute", inset: 0, borderRadius: "50%",
        background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.15), transparent 60%)",
      }} />
    </div>
  );
}

// ─── MARKET WORLD MODEL ───────────────────────────────────────────────────────
function MarketWorldModel({ ctx, pnl = null, panelState = 'watching' }) {
  const {
    support, resistance, currentPrice,
    entryPrice, targetPrice, stopPrice,
    bullStrength, bearStrength,
    modelDRL, modelDT, modelLeadership, timingSync, regime,
    consensus, flipRate,
  } = ctx;

  const hasPriceLevels = support != null && resistance != null && support !== resistance;

  // Auto-range: expand to include stop/target/entry if they fall outside support–resistance
  const allPrices = [support, resistance, stopPrice, entryPrice, targetPrice, currentPrice]
    .filter((v) => v != null && v > 0);
  const barMin = hasPriceLevels ? Math.min(...allPrices) : support ?? 0;
  const barMax = hasPriceLevels ? Math.max(...allPrices) : resistance ?? 1;
  // Add 5% padding so labels don't clip at edges
  const padding = (barMax - barMin) * 0.05 || 0.5;
  const rangeMin = barMin - padding;
  const rangeMax = barMax + padding;
  const range    = rangeMax - rangeMin || 1;

  const pos = (v) => v != null ? Math.min(1, Math.max(0, (v - rangeMin) / range)) : null;

  const pricePos  = hasPriceLevels ? pos(currentPrice || support) : 0.5;
  const entryPos  = hasPriceLevels && entryPrice  ? pos(entryPrice)  : null;
  const targetPos = hasPriceLevels && targetPrice ? pos(targetPrice) : null;
  const stopPos   = hasPriceLevels && stopPrice   ? pos(stopPrice)   : null;
  const supportPos   = hasPriceLevels ? pos(support)    : null;
  const resistancePos= hasPriceLevels ? pos(resistance) : null;

  // ── Dot color based on P&L (±$0.02 threshold) ──────────────────────────────
  const isActive = panelState === "active";
  const dotColor = !isActive || pnl == null
    ? C.cyan
    : pnl >  0.02 ? C.green
    : pnl < -0.02 ? C.red
    : C.cyan;

  // ── Trail history: last 3 distinct pricePos values ───────────────────────
  const trailRef   = useRef([]);
  const lastPosRef = useRef(pricePos);
  if (isActive && hasPriceLevels && Math.abs(pricePos - lastPosRef.current) > 0.005) {
    trailRef.current = [lastPosRef.current, ...trailRef.current].slice(0, 3);
    lastPosRef.current = pricePos;
  }
  if (!isActive) { trailRef.current = []; lastPosRef.current = pricePos; }

  const leaderColor = { chaotic: C.red, drl_leading: C.cyan, dt_leading: C.blue, aligned: C.green }[modelLeadership] || C.muted;
  const drlColor = modelDRL === "bullish" ? C.green : modelDRL === "bearish" ? C.red : C.muted;
  const dtColor  = modelDT  === "bullish" ? C.green : modelDT  === "bearish" ? C.red : C.muted;
  const flipColor = (flipRate ?? 0) < 0.15 ? C.green : (flipRate ?? 0) < 0.30 ? C.amber : C.red;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* Price bar */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
          <div style={{ display: "flex", gap: "8px" }}>
            {hasPriceLevels
              ? <>
                  {stopPrice  && <span style={{ fontSize: "9px", color: C.red,   fontFamily: "'DM Mono', monospace" }}>STOP {fmt2(stopPrice)}</span>}
                  <span style={{ fontSize: "9px", color: C.muted, fontFamily: "'DM Mono', monospace" }}>S {fmt2(support)}</span>
                </>
              : <span style={{ fontSize: "9px", color: C.muted, fontFamily: "'DM Mono', monospace" }}>No level data</span>
            }
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            {hasPriceLevels && <>
              <span style={{ fontSize: "9px", color: C.muted,  fontFamily: "'DM Mono', monospace" }}>R {fmt2(resistance)}</span>
              {targetPrice && <span style={{ fontSize: "9px", color: C.green, fontFamily: "'DM Mono', monospace" }}>TP {fmt2(targetPrice)}</span>}
            </>}
          </div>
        </div>
        <div style={{ position: "relative", height: "36px", background: C.s3, borderRadius: "6px", overflow: "visible" }}>
          {/* Danger zone */}
          {stopPos !== null && entryPos !== null && (
            <div style={{ position: "absolute", left: `${stopPos * 100}%`, width: `${(entryPos - stopPos) * 100}%`, top: 0, bottom: 0, background: `${C.red}15`, borderRadius: "4px 0 0 4px" }} />
          )}
          {/* Profit zone */}
          {entryPos !== null && targetPos !== null && (
            <div style={{ position: "absolute", left: `${entryPos * 100}%`, width: `${(targetPos - entryPos) * 100}%`, top: 0, bottom: 0, background: `${C.green}15` }} />
          )}
          {/* ── Level lines with staggered labels ────────────────────────── */}
          {/* Stagger map: index → above(true) or below(false) by position order */}
          {(() => {
            // Build ordered list of levels left→right so we can alternate above/below
            const levels = [
              stopPos   != null && { pos: stopPos,    color: C.red,   label: "STOP",  solid: true  },
              supportPos!= null && { pos: supportPos, color: "#f59e0b", label: "S",   solid: false },
              entryPos  != null && { pos: entryPos,   color: C.amber, label: "ENTRY", solid: true  },
              resistancePos != null && { pos: resistancePos, color: "#60a5fa", label: "R", solid: false },
              targetPos != null && { pos: targetPos,  color: C.green, label: "TP",    solid: true  },
            ].filter(Boolean).sort((a, b) => a.pos - b.pos);

            return levels.map((lv, i) => {
              const above = i % 2 === 0;  // alternate: 0=above, 1=below, 2=above …
              return (
                <div key={lv.label} style={{
                  position: "absolute",
                  left: `${lv.pos * 100}%`,
                  top: 0, bottom: 0,
                  width: lv.solid ? "2px" : "1.5px",
                  background: lv.solid
                    ? lv.color
                    : undefined,
                  backgroundImage: lv.solid
                    ? undefined
                    : `repeating-linear-gradient(to bottom, ${lv.color}90 0px, ${lv.color}90 4px, transparent 4px, transparent 7px)`,
                  opacity: lv.solid ? 0.85 : 1,
                  zIndex: 1,
                }}>
                  <span style={{
                    position: "absolute",
                    ...(above
                      ? { top: "-15px" }
                      : { bottom: "-15px" }),
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: "8px",
                    color: lv.color,
                    fontFamily: "monospace",
                    whiteSpace: "nowrap",
                    fontWeight: 700,
                    letterSpacing: "0.2px",
                  }}>{lv.label}</span>
                </div>
              );
            });
          })()}

          {/* ── Trail ghosts ─────────────────────────────────────────────── */}
          {isActive && trailRef.current.map((tp, i) => {
            const opacity = [0.45, 0.25, 0.10][i];
            const size    = [11, 9, 7][i];
            return (
              <div key={i} style={{
                position: "absolute",
                left: `${tp * 100}%`,
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: `${size}px`, height: `${size}px`,
                borderRadius: "50%",
                background: dotColor,
                opacity,
                animation: `bipTrailFade 0.6s ease-out forwards`,
                zIndex: 1,
                pointerEvents: "none",
              }} />
            );
          })}

          {/* ── Price dot + ripple ───────────────────────────────────────── */}
          {/* Ripple ring */}
          {isActive && (
            <div style={{
              position: "absolute",
              left: `${pricePos * 100}%`,
              top: "50%",
              width: "14px", height: "14px",
              borderRadius: "50%",
              border: `2px solid ${dotColor}`,
              animation: "bipRipple 1.4s ease-out infinite",
              zIndex: 1,
              pointerEvents: "none",
            }} />
          )}
          {/* Dot */}
          <div
            className="bip-price-dot"
            style={{
              position: "absolute", left: `${pricePos * 100}%`, top: "50%",
              transform: "translate(-50%, -50%)",
              width: "14px", height: "14px", borderRadius: "50%",
              background: dotColor,
              boxShadow: `0 0 ${isActive ? 10 : 6}px ${dotColor}${isActive ? "cc" : "80"}`,
              zIndex: 3,
            }}
          >
            <span style={{
              position: "absolute", bottom: "18px", left: "50%",
              transform: "translateX(-50%)",
              fontSize: "9px", color: dotColor, fontFamily: "monospace",
              whiteSpace: "nowrap", fontWeight: 700,
            }}>
              ${typeof currentPrice === "number" ? currentPrice.toFixed(2) : "—"}
            </span>
          </div>
        </div>
      </div>

      {/* Model intelligence row */}
      <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
        {/* DRL */}
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>DRL</span>
          <span style={{ fontSize: "12px", color: drlColor }}>{modelDRL === "bullish" ? "▲" : modelDRL === "bearish" ? "▼" : "—"}</span>
        </div>

        {/* Leadership */}
        <Tooltip text="Consensus">
          <span style={{
            fontSize: "9px", padding: "2px 7px",
            background: `${leaderColor}15`, color: leaderColor,
            border: `1px solid ${leaderColor}30`,
            borderRadius: "3px", fontFamily: "monospace", fontWeight: 700,
            cursor: "help", textTransform: "uppercase",
          }}>
            {(modelLeadership || "unknown").replace("_", " ")}
          </span>
        </Tooltip>

        {/* DT */}
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>DT</span>
          <span style={{ fontSize: "12px", color: dtColor }}>{modelDT === "bullish" ? "▲" : modelDT === "bearish" ? "▼" : "—"}</span>
        </div>

        {/* Timing sync */}
        <Tooltip text="Timing Sync">
          <div style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "help" }}>
            <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: timingSync ? C.green : C.amber }} />
            <span style={{ fontSize: "9px", color: timingSync ? C.green : C.amber, fontFamily: "monospace" }}>
              {timingSync ? "synced" : "unsynced"}
            </span>
          </div>
        </Tooltip>

        {/* Regime flip rate */}
        {flipRate != null && (
          <Tooltip text="Flip Rate">
            <div style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "help" }}>
              <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>FLIP</span>
              <div style={{ width: "50px", height: "5px", background: C.s3, borderRadius: "3px", overflow: "hidden" }}>
                <div style={{ width: `${typeof flipRate === "number" ? (flipRate * 100) : 0}%`, height: "100%", background: flipColor }} />
              </div>
              <span style={{ fontSize: "9px", color: flipColor, fontFamily: "monospace" }}>
                {typeof flipRate === "number" ? pct(flipRate) : (flipRate || "—")}
              </span>
            </div>
          </Tooltip>
        )}

        {/* Regime pill */}
        {regime && (
          <span style={{
            fontSize: "9px", padding: "2px 7px",
            background: C.s3, color: C.muted,
            border: `1px solid ${C.border}`,
            borderRadius: "3px", fontFamily: "monospace",
          }}>
            {regime || "—"}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── STOCK → OPTIONS LINK ────────────────────────────────────────────────────
function StockOptionsLink({ stock, options }) {
  if (!stock) return null;
  const pnlColor = (stock.pnl || 0) >= 0 ? C.green : C.red;
  return (
    <div style={{
      display: "flex", alignItems: "stretch",
      background: C.s2, borderRadius: "8px",
      border: `1px solid ${C.border}`, overflow: "hidden",
    }}>
      {/* Stock side */}
      <div style={{ flex: 1, padding: "10px 14px" }}>
        <div style={{ fontSize: "9px", color: C.muted, fontFamily: "'DM Mono', monospace", marginBottom: "5px", letterSpacing: "1px" }}>STOCK SIGNAL</div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
          <span style={{ fontSize: "9px", padding: "1px 5px", background: stock.side === "LONG" ? C.gDim : C.rDim, color: stock.side === "LONG" ? C.green : C.red, borderRadius: "3px", fontFamily: "monospace", fontWeight: 700 }}>{stock.side}</span>
          <span style={{ fontSize: "12px", fontWeight: 700, color: C.text, fontFamily: "'Syne', sans-serif" }}>
            {stock.qty} shares
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px" }}>
          {[
            ["Entry",  `$${fmt2(stock.entry)}`],
            ["Target", `$${fmt2(stock.target)}`],
            ["Stop",   `$${fmt2(stock.stop)}`],
            ["P&L",    stock.closed
              ? `$${fmt2(stock.pnl)}`
              : `${(stock.pnl || 0) >= 0 ? "+" : ""}$${fmt2(stock.pnl)}`],
          ].map(([l, v]) => (
            <div key={l}>
              <span style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace" }}>{l} </span>
              <span style={{ fontSize: "10px", fontWeight: 700, color: l === "P&L" ? pnlColor : C.text, fontFamily: "monospace" }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Arrow */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 10px", gap: "4px", borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, background: C.s3 }}>
        <span style={{ fontSize: "16px", color: C.cyan }}>→</span>
        <span style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace", textAlign: "center", maxWidth: "40px", lineHeight: 1.3 }}>drives</span>
      </div>

      {/* Options side */}
      {options ? (
        <Tooltip text={options.strategy || "DEBIT_SPREAD"}>
          <div style={{ flex: 1, padding: "10px 14px", cursor: "help" }}>
            <div style={{ fontSize: "9px", color: C.muted, fontFamily: "'DM Mono', monospace", marginBottom: "5px", letterSpacing: "1px" }}>OPTIONS STRATEGY</div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
              <span style={{ fontSize: "12px", fontWeight: 700, color: C.cyan, fontFamily: "'Syne', sans-serif" }}>{options.strategy || "—"}</span>
              <span style={{ fontSize: "9px", padding: "2px 6px", background: options.status === "ACTIVE" ? C.gDim : C.cDim, color: options.status === "ACTIVE" ? C.green : C.cyan, borderRadius: "3px", fontFamily: "monospace", fontWeight: 700 }}>{options.status}</span>
            </div>
            {options.estimatedValue && <div style={{ fontSize: "10px", color: C.green, fontFamily: "monospace", marginBottom: "4px" }}>{options.estimatedValue}</div>}
            {options.pnl != null && <div style={{ fontSize: "13px", fontWeight: 700, color: C.green, fontFamily: "'Syne', sans-serif" }}>+${options.pnl}</div>}
            <div style={{ marginTop: "4px", fontSize: "9px", color: C.muted, fontFamily: "monospace", lineHeight: 1.4 }}>{options.note || options.closedBy || ""}</div>
          </div>
        </Tooltip>
      ) : (
        <div style={{ flex: 1, padding: "10px 14px", display: "flex", alignItems: "center" }}>
          <span style={{ fontSize: "9px", color: C.subtle, fontFamily: "monospace" }}>Options data not available</span>
        </div>
      )}
    </div>
  );
}

// ─── CONFIDENCE ARC ───────────────────────────────────────────────────────────
function ConfidenceArc({ thinking }) {
  if (!thinking) return null;
  const { initial, combined, final } = thinking;
  const rose = (final.conf || 0) > (initial.conf || 0);
  const arcColor = rose ? C.green : (final.conf || 0) < (initial.conf || 0) ? C.amber : C.cyan;
  const nodes = [
    { label: "INITIAL",  conf: initial.conf,  decision: initial.decision,  sub: "chart + patterns" },
    { label: "COMBINED", conf: null,           decision: combined.rec,      sub: "model integration" },
    { label: "FINAL",    conf: final.conf,     decision: final.decision,    sub: final.override ? (final.overrideReason || "rule override") : "clean decision" },
  ];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      {nodes.map((n, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
            <span style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace", letterSpacing: "0.5px" }}>{n.label}</span>
            <div style={{
              width: "40px", height: "40px", borderRadius: "50%",
              background: C.s3,
              border: `2px solid ${i === 2 ? arcColor : C.border}`,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              boxShadow: i === 2 ? `0 0 14px ${arcColor}40` : "none",
            }}>
              {n.conf != null
                ? <span style={{ fontSize: "10px", fontWeight: 700, color: arcColor, fontFamily: "monospace" }}>{Math.round(n.conf * 100)}</span>
                : <span style={{ fontSize: "8px", color: C.muted }}>—</span>}
            </div>
            <span style={{ fontSize: "8px", color: i === 2 ? arcColor : C.muted, fontFamily: "monospace", fontWeight: i === 2 ? 700 : 400 }}>{n.decision}</span>
            <span style={{ fontSize: "7px", color: C.subtle, fontFamily: "monospace", textAlign: "center", maxWidth: "50px", lineHeight: 1.2 }}>{n.sub}</span>
          </div>
          {i < 2 && (
            <div style={{ display: "flex", alignItems: "center", margin: "0 2px", marginTop: "-12px" }}>
              <div style={{ width: "16px", height: "2px", background: `linear-gradient(90deg, ${C.border}, ${arcColor}60)` }} />
              <div style={{ width: 0, height: 0, borderTop: "3px solid transparent", borderBottom: "3px solid transparent", borderLeft: `5px solid ${arcColor}60` }} />
            </div>
          )}
        </div>
      ))}
      {final.override && (
        <div style={{ marginLeft: "8px", padding: "6px 10px", background: C.aDim, border: `1px solid ${C.amber}40`, borderRadius: "6px" }}>
          <div style={{ fontSize: "8px", color: C.amber, fontFamily: "monospace", fontWeight: 700 }}>RULE OVERRIDE</div>
          <div style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace" }}>{final.overrideReason || "rule applied"}</div>
        </div>
      )}
    </div>
  );
}

// ─── VOTE BARS ────────────────────────────────────────────────────────────────
function VoteBars({ votes }) {
  const colors = { hold_position: C.cyan, exit_partial: C.amber, close_now: C.red };
  const labels = { hold_position: "HOLD", exit_partial: "EXIT", close_now: "CLOSE" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
      {Object.entries(votes).map(([k, v]) => (
        <div key={k} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ width: "34px", fontSize: "9px", color: colors[k] || C.muted, fontFamily: "monospace", fontWeight: 700 }}>{labels[k] || k.toUpperCase()}</span>
          <div style={{ flex: 1, height: "5px", background: C.s3, borderRadius: "3px", overflow: "hidden" }}>
            <div style={{ width: pct(v), height: "100%", background: colors[k] || C.cyan, borderRadius: "3px" }} />
          </div>
          <span style={{ width: "28px", fontSize: "9px", color: colors[k] || C.muted, fontFamily: "monospace", textAlign: "right" }}>{pct(v)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── WEIGHT BARS ──────────────────────────────────────────────────────────────
function WeightBars({ weights }) {
  const colors = { patterns: C.purple, levels: C.blue, microstructure: C.cyan, fft: C.amber };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {Object.entries(weights).map(([k, v]) => (
        <div key={k} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ width: "68px", fontSize: "9px", color: colors[k] || C.muted, fontFamily: "monospace", textTransform: "uppercase" }}>{k}</span>
          <div style={{ flex: 1, height: "4px", background: C.s3, borderRadius: "2px", overflow: "hidden" }}>
            <div style={{ width: pct(v), height: "100%", background: colors[k] || C.muted }} />
          </div>
          <span style={{ width: "28px", fontSize: "9px", color: colors[k] || C.muted, fontFamily: "monospace", textAlign: "right" }}>{pct(v)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── PATTERN ROW ──────────────────────────────────────────────────────────────
function PatternRow({ p }) {
  const axes = [["M", p.tags.macro], ["m", p.tags.micro], ["P", p.tags.position]];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "7px 10px", background: C.s3, borderRadius: "6px" }}>
      <div style={{ display: "flex", gap: "4px" }}>
        {axes.map(([label, val]) => (
          <div key={label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
            <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: tagColor(val), boxShadow: val === "against" ? `0 0 5px ${C.red}` : "none" }} />
            <span style={{ fontSize: "7px", color: C.muted, fontFamily: "monospace" }}>{label}</span>
          </div>
        ))}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: "9px", color: C.text, fontFamily: "monospace" }}>{p.name}</div>
        <div style={{ display: "flex", gap: "4px", marginTop: "3px", flexWrap: "wrap" }}>
          <Tooltip text="Formation">
            <span style={{ fontSize: "8px", color: (p.formed || 0) > 0.7 ? C.green : C.amber, fontFamily: "monospace" }}>{pct(p.formed)} formed</span>
          </Tooltip>
          <span style={{ fontSize: "8px", color: C.subtle }}>·</span>
          <Tooltip text="Vol Confirmed">
            <span style={{ fontSize: "8px", color: p.volConf ? C.green : C.red, fontFamily: "monospace" }}>{p.volConf ? "vol ✓" : "no vol ✗"}</span>
          </Tooltip>
        </div>
      </div>
      <div style={{ fontSize: "10px", fontWeight: 700, color: confColor(bucketFromScore(p.conf)), fontFamily: "monospace" }}>{pct(p.conf)}</div>
    </div>
  );
}

// ─── TREND STRIP ─────────────────────────────────────────────────────────────
function TrendStrip({ checks }) {
  if (!checks || checks.length === 0) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 12px", background: C.s3, borderRadius: "6px" }}>
      <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", marginRight: "4px" }}>EVAL HISTORY</span>
      {checks.map((c, i) => {
        const bucket = c.conf > 0.70 ? "HIGH" : c.conf > 0.50 ? "MEDIUM" : "LOW";
        return (
          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
            <div style={{
              width: "28px", height: "28px", borderRadius: "50%",
              background: confColor(bucket) + "20",
              border: `2px solid ${confColor(bucket)}`,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1px",
            }}>
              <span style={{ fontSize: "8px", fontWeight: 700, color: confColor(bucket), fontFamily: "monospace", lineHeight: 1 }}>{Math.round((c.conf || 0) * 100)}</span>
              {c.flags > 0 && <span style={{ fontSize: "7px", color: C.red, lineHeight: 1 }}>⚑{c.flags}</span>}
            </div>
            <span style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace" }}>{c.time}</span>
            <span style={{ fontSize: "7px", color: C.subtle, fontFamily: "monospace" }}>{c.decision}</span>
          </div>
        );
      })}
      <div style={{ flex: 1, height: "1px", background: `linear-gradient(90deg, ${C.border}, transparent)`, marginLeft: "4px" }} />
      <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>next: 5m</span>
    </div>
  );
}

// ─── DASHBOARD VIEW ───────────────────────────────────────────────────────────
function DashboardView({ data }) {
  const { orbColor, orbPulse, headline, subheadline, todayStats, marketContext, stock, options, ev, reactiveHistory, panelState } = data;

  const evNeg = ev?.performance?.ev != null && ev.performance.ev < 0;

  // Local timer state for timeframe lock (avoids memoization loops)
  const [lockSecs, setLockSecs] = useState(
    ev?.timeframeLock?.active && ev.timeframeLock.remainingMin
      ? ev.timeframeLock.remainingMin * 60
      : 0
  );
  const prevLockActive = useRef(ev?.timeframeLock?.active);

  const lockActive = panelState === "active" && !!ev?.timeframeLock?.active;

  useEffect(() => {
    if (lockActive && !prevLockActive.current) {
      setLockSecs((ev.timeframeLock.remainingMin || 0) * 60);
    }
    if (!lockActive) {
      setLockSecs(0);  // immediately zero out when position closes
    }
    prevLockActive.current = lockActive;
  }, [lockActive, ev?.timeframeLock?.remainingMin]);

  useEffect(() => {
    if (!lockActive) return;
    const t = setInterval(() => setLockSecs((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [lockActive]);

  const lockMins = Math.floor(lockSecs / 60);
  const lockSecs2 = lockSecs % 60;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

      {/* Row 1: Orb + Headline */}
      <div style={{
        display: "flex", alignItems: "center", gap: "16px",
        padding: "16px 20px", background: C.s2, borderRadius: "10px",
        border: `1px solid ${C.border}`, borderLeft: `3px solid ${orbColor}`,
      }}>
        <StatusOrb color={orbColor} pulse={orbPulse} size={52} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "17px", fontWeight: 700, color: C.text, fontFamily: "'Syne', sans-serif", lineHeight: 1.3, marginBottom: "5px" }}>
            {headline}
          </div>
          <div style={{ fontSize: "12px", color: C.muted, fontFamily: "'DM Mono', monospace" }}>
            {subheadline}
          </div>
        </div>
        {/* Today stats pills */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-end" }}>
          <div style={{ display: "flex", gap: "6px" }}>
            {[
              ["evaluated", todayStats.evaluated, C.muted],
              ["proceeded", todayStats.proceeded, C.green],
              ["skipped",   todayStats.skipped,   C.subtle],
            ].map(([l, v, col]) => (
              <div key={l} style={{ padding: "3px 8px", background: C.s3, borderRadius: "4px", border: `1px solid ${C.border}` }}>
                <span style={{ fontSize: "11px", fontWeight: 700, color: col, fontFamily: "monospace" }}>{v}</span>
                <span style={{ fontSize: "9px",  color: C.muted, fontFamily: "monospace" }}> {l}</span>
              </div>
            ))}
          </div>
          {ev?.flags?.length > 0 && (
            <div style={{ display: "flex", gap: "4px" }}>
              {ev.flags.map((f) => (
                <span key={f} style={{ fontSize: "9px", padding: "2px 7px", background: C.aDim, color: C.amber, borderRadius: "3px", fontFamily: "monospace", border: `1px solid ${C.amber}30` }}>
                  ⚠ {f.replace("warning:", "")}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Row 2: World model + key metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "14px" }}>
        {/* World model */}
        <div style={{ padding: "14px 16px", background: C.s2, borderRadius: "10px", border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", letterSpacing: "1px", marginBottom: "12px" }}>MARKET CONTEXT</div>
          <MarketWorldModel ctx={marketContext} pnl={stock?.pnl ?? null} panelState={panelState} />
        </div>

        {/* Key metrics strip */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: "130px" }}>
          {/* Confidence */}
          <div style={{ padding: "10px 14px", background: C.s2, borderRadius: "8px", border: `1px solid ${C.border}`, flex: 1 }}>
            <div style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", marginBottom: "4px" }}>CONFIDENCE</div>
            <div style={{ fontSize: "20px", fontWeight: 700, color: confColor(ev?.bucket), fontFamily: "'Syne', sans-serif", lineHeight: 1 }}>
              {ev?.bucket || "—"}
            </div>
            <div style={{ fontSize: "10px", color: C.muted, fontFamily: "monospace", marginTop: "2px" }}>
              {ev?.confidence != null ? `${Math.round(ev.confidence * 100)}%` : ""}
            </div>
          </div>

          {/* EV */}
          {ev?.performance?.ev != null && (
            <Tooltip text="EV">
              <div style={{
                padding: "10px 14px", background: C.s2, borderRadius: "8px",
                border: `1px solid ${evNeg ? C.amber + "40" : C.border}`,
                flex: 1, cursor: "help",
              }}>
                <div style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", marginBottom: "4px" }}>EXP. VALUE</div>
                <div style={{ fontSize: "16px", fontWeight: 700, color: evNeg ? C.amber : C.green, fontFamily: "'Syne', sans-serif", lineHeight: 1 }}>
                  {evNeg ? "-" : "+"}${Math.abs(ev.performance.ev).toFixed(3)}
                </div>
                {!ev.performance.supports && (
                  <div style={{ fontSize: "8px", color: C.amber, fontFamily: "monospace", marginTop: "3px" }}>history mixed</div>
                )}
              </div>
            </Tooltip>
          )}

          {/* Timeframe lock */}
          {ev?.timeframeLock?.active && (
            <Tooltip text="Timeframe Lock">
              <div style={{ padding: "10px 14px", background: C.aDim, borderRadius: "8px", border: `1px solid ${C.amber}40`, flex: 1, cursor: "help" }}>
                <div style={{ fontSize: "9px", color: C.amber, fontFamily: "monospace", marginBottom: "4px" }}>LOCK EXPIRES</div>
                <div style={{ fontSize: "18px", fontWeight: 700, color: C.amber, fontFamily: "'Syne', sans-serif", lineHeight: 1 }}>
                  {lockMins}:{lockSecs2.toString().padStart(2, "0")}
                </div>
              </div>
            </Tooltip>
          )}

          {/* Risk flags count */}
          {ev?.riskFlags && (
            <div style={{
              padding: "10px 14px", background: C.s2, borderRadius: "8px",
              border: `1px solid ${rfCount(ev.riskFlags) >= 4 ? C.red + "40" : C.border}`,
              flex: 1,
            }}>
              <div style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", marginBottom: "4px" }}>FLAGS</div>
              <div style={{ fontSize: "20px", fontWeight: 700, color: rfCount(ev.riskFlags) >= 4 ? C.red : C.muted, fontFamily: "'Syne', sans-serif", lineHeight: 1 }}>
                {rfCount(ev.riskFlags)}/5
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Stock → Options */}
      {stock && <StockOptionsLink stock={stock} options={options} />}

      {/* Row 4: Trend strip */}
      {reactiveHistory.length > 0 && <TrendStrip checks={reactiveHistory} />}
    </div>
  );
}

// ─── ANALYSIS VIEW ────────────────────────────────────────────────────────────
function AnalysisView({ data }) {
  const { marketContext, stock, options, ev, reactiveHistory, panelState } = data;
  const evNeg = ev?.performance?.ev != null && ev.performance.ev < 0;

  const [lockSecs, setLockSecs] = useState(
    ev?.timeframeLock?.active && ev.timeframeLock.remainingMin
      ? ev.timeframeLock.remainingMin * 60
      : 0
  );

  useEffect(() => {
    if (!ev?.timeframeLock?.active) return;
    const t = setInterval(() => setLockSecs((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [ev?.timeframeLock?.active]);

  const lockMins  = Math.floor(lockSecs / 60);
  const lockSecs2 = lockSecs % 60;

  const Card = ({ title, accent, children, tip }) => (
    <div style={{ background: C.s2, borderRadius: "8px", border: `1px solid ${C.border}`, overflow: "hidden" }}>
      <div style={{
        padding: "8px 12px", background: C.s3,
        borderBottom: `1px solid ${C.border}`, borderLeft: `3px solid ${accent || C.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <span style={{ fontSize: "9px", fontWeight: 700, color: accent || C.muted, fontFamily: "monospace", letterSpacing: "1px", textTransform: "uppercase" }}>{title}</span>
        {tip && <Tooltip text={tip}><span style={{ fontSize: "9px", color: C.subtle, cursor: "help" }}>?</span></Tooltip>}
      </div>
      <div style={{ padding: "12px" }}>{children}</div>
    </div>
  );

  if (!ev) {
    return (
      <div style={{ padding: "24px", textAlign: "center", display: "flex", flexDirection: "column", gap: "8px", alignItems: "center" }}>
        <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: C.muted, opacity: 0.5 }} />
        <span style={{ fontSize: "11px", color: C.muted, fontFamily: "monospace" }}>
          Waiting for first agent evaluation...
        </span>
        <span style={{ fontSize: "9px", color: C.subtle, fontFamily: "monospace" }}>
          Analysis data appears after the first BUY/SELL signal bar
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>

      {/* Context banner — shows when not actively in a position */}
      {panelState !== "active" && (
        <div style={{
          padding: "7px 12px", borderRadius: "6px",
          background: C.s3, border: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", gap: "8px",
        }}>
          <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: panelState === "post_trade" ? C.amber : C.cyan, flexShrink: 0 }} />
          <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>
            {panelState === "post_trade"
              ? "LAST REACTIVE EVAL — position just closed"
              : "LAST PROCEED EVAL — showing most recent entry decision"}
          </span>
          {ev.timestamp && (
            <span style={{ fontSize: "9px", color: C.subtle, fontFamily: "monospace", marginLeft: "auto" }}>
              {new Date(ev.timestamp).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })}
            </span>
          )}
        </div>
      )}

      {/* Analysis header strip */}
      <div style={{ display: "flex", gap: "8px", alignItems: "center", padding: "8px 14px", background: C.s3, borderRadius: "8px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>PATH</span>
        <span style={{
          fontSize: "10px", fontWeight: 700, fontFamily: "monospace",
          color:      ev.path === "fast_path" ? C.green : ev.path === "emergency" ? C.red : C.blue,
          padding:    "2px 8px",
          background: ev.path === "fast_path" ? C.gDim  : ev.path === "emergency" ? C.rDim  : C.bDim,
          borderRadius: "3px",
        }}>
          {ev.path === "fast_path" ? "⚡ FAST PATH" : ev.path === "full_path" ? "🔄 FULL PATH · 4 stages" : "🚨 EMERGENCY"}
        </span>

        <div style={{ width: "1px", height: "16px", background: C.border }} />
        <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>DECISION</span>
        <span style={{ fontSize: "10px", fontWeight: 700, color: confColor(ev.bucket), fontFamily: "monospace" }}>{ev.decision}</span>

        <div style={{ width: "1px", height: "16px", background: C.border }} />
        <span style={{ fontSize: "10px", color: confColor(ev.bucket), fontFamily: "monospace" }}>
          {ev.bucket || "—"}{ev.confidence != null ? ` · ${Math.round(ev.confidence * 100)}%` : ""}
          {ev.ci ? ` [${Math.round(ev.ci[0] * 100)}–${Math.round(ev.ci[1] * 100)}%]` : ""}
        </span>

        {ev.timeframeLock?.active && (
          <>
            <div style={{ width: "1px", height: "16px", background: C.border }} />
            <Tooltip text="Timeframe Lock">
              <span style={{ fontSize: "10px", color: C.amber, fontFamily: "monospace", cursor: "help" }}>
                ⏱ LOCK {lockMins}:{lockSecs2.toString().padStart(2, "0")}
              </span>
            </Tooltip>
          </>
        )}

        {ev.performance?.ev != null && (
          <>
            <div style={{ width: "1px", height: "16px", background: C.border }} />
            <Tooltip text="EV">
              <span style={{ fontSize: "10px", color: evNeg ? C.amber : C.green, fontFamily: "monospace", cursor: "help" }}>
                EV {evNeg ? "-" : "+"}${Math.abs(ev.performance.ev).toFixed(4)} {!ev.performance.supports ? "⚠" : ""}
              </span>
            </Tooltip>
          </>
        )}
      </div>

      {/* Thinking arc */}
      {ev.thinking && (
        <Card title="Thinking Evolution — 4 Stage Deliberation" accent={C.purple}>
          <ConfidenceArc thinking={ev.thinking} />
        </Card>
      )}

      {/* 3-col grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>

        {/* Vote split + evidence weights */}
        {ev.ruleVotes && (
          <Card title="Internal Vote Split" accent={C.cyan} tip="Vote Split">
            <VoteBars votes={ev.ruleVotes} />
            {ev.evidenceWeights && (
              <>
                <div style={{ height: "1px", background: C.border, margin: "10px 0" }} />
                <div style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", marginBottom: "6px", letterSpacing: "0.5px" }}>EVIDENCE WEIGHTS</div>
                <Tooltip text="Evidence Weights"><div><WeightBars weights={ev.evidenceWeights} /></div></Tooltip>
              </>
            )}
          </Card>
        )}

        {/* Risk signals */}
        <Card title="Risk Signals" accent={rfCount(ev.riskFlags) >= 4 ? C.red : C.amber}>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px", marginBottom: "10px" }}>
            {Object.entries(ev.riskFlags || {}).map(([k, v]) => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{
                  width: "7px", height: "7px", borderRadius: "50%",
                  background: v ? C.red : C.subtle,
                  boxShadow: v ? `0 0 5px ${C.red}` : "none",
                  flexShrink: 0,
                }} />
                <span style={{ fontSize: "9px", color: v ? C.text : C.muted, fontFamily: "monospace" }}>
                  {k.replace(/([A-Z])/g, " $1").toLowerCase()}
                </span>
              </div>
            ))}
          </div>
          <div style={{ height: "1px", background: C.border, margin: "8px 0" }} />
          <Tooltip text="Timing Sync">
            <div style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "help" }}>
              <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: marketContext.timingSync ? C.green : C.amber }} />
              <span style={{ fontSize: "9px", color: marketContext.timingSync ? C.green : C.amber, fontFamily: "monospace" }}>
                timing {marketContext.timingSync ? "synced" : "unsynced"}
              </span>
            </div>
          </Tooltip>
          {ev.primarySignal && (
            <div style={{ fontSize: "9px", color: ev.primarySignal === "conflicted" ? C.red : C.cyan, fontFamily: "monospace", marginTop: "4px" }}>
              signal: {ev.primarySignal}
            </div>
          )}
        </Card>

        {/* Historical performance */}
        {ev.performance && (
          <Card title="Historical Performance" accent={evNeg ? C.amber : C.green} tip="EV">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "7px" }}>
              {[
                ["Win Rate",   pct(ev.performance.successRate), (ev.performance.successRate || 0) >= 0.6 ? C.green : C.amber],
                ["Target Hit", pct(ev.performance.targetHit  || 0), C.green],
                ["Stop Hit",   pct(ev.performance.stopHit    || 0), C.green],
                ["Avg P&L",    `$${fmt2(ev.performance.avgPnl || 0)}`, C.green],
                ["Exp. Value", `${evNeg ? "-" : "+"}$${Math.abs(ev.performance.ev || 0).toFixed(4)}`, evNeg ? C.amber : C.green],
                ["Similarity", pct(ev.performance.similarity || 0), C.cyan],
              ].map(([l, v, col]) => (
                <div key={l}>
                  <div style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace" }}>{l}</div>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: col, fontFamily: "monospace" }}>{v}</div>
                </div>
              ))}
            </div>
            {!ev.performance.supports && (
              <div style={{ marginTop: "8px", padding: "6px 8px", background: C.aDim, borderRadius: "5px", border: `1px solid ${C.amber}30` }}>
                <div style={{ fontSize: "9px", color: C.amber, fontFamily: "monospace", fontWeight: 700 }}>⚠ History does not support this decision</div>
                <div style={{ fontSize: "8px", color: C.muted, fontFamily: "monospace", marginTop: "2px" }}>Bot holding on rules, not on edge</div>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Patterns */}
      {ev.patterns?.length > 0 && (
        <Card title="Candlestick Pattern Analysis" accent={C.purple}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
            {ev.patterns.map((p, i) => <PatternRow key={i} p={p} />)}
          </div>
          {ev.patternRule && (
            <div style={{ marginTop: "8px", padding: "6px 10px", background: C.s3, borderRadius: "5px" }}>
              <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace" }}>Rule applied: </span>
              <span style={{ fontSize: "9px", color: C.cyan, fontFamily: "monospace", fontStyle: "italic" }}>"{ev.patternRule}"</span>
            </div>
          )}
        </Card>
      )}

      {/* Memory */}
      {ev.memory?.cases > 0 && (
        <Card title={`Memory · ${ev.memory.cases} cases · ${pct(ev.memory.similarity)} match`} accent={C.cyan} tip="STM vs LTM">
          <div style={{ display: "flex", gap: "16px", marginBottom: "8px" }}>
            {[["Recent (STM)", ev.memory.recent || 0, ev.performance?.stmRate], ["Historical (LTM)", ev.memory.historical || 0, ev.performance?.ltmRate]].map(([label, n, rate]) => (
              <div key={label} style={{ flex: 1 }}>
                <div style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", marginBottom: "4px" }}>{label} · {n} cases</div>
                <div style={{ height: "5px", background: C.s3, borderRadius: "3px", overflow: "hidden" }}>
                  <div style={{ width: pct(rate || 0), height: "100%", background: (rate || 0) >= 0.6 ? C.green : C.amber }} />
                </div>
                <div style={{ fontSize: "10px", fontWeight: 700, color: C.text, fontFamily: "monospace", marginTop: "3px" }}>{pct(rate || 0)}</div>
              </div>
            ))}
          </div>
          {ev.memory.note && (
            <div style={{ fontSize: "10px", color: C.muted, fontFamily: "'DM Mono', monospace", lineHeight: 1.6 }}>{ev.memory.note}</div>
          )}
        </Card>
      )}

      {/* Trend strip */}
      {reactiveHistory.length > 0 && <TrendStrip checks={reactiveHistory} />}

      {/* Stock → Options */}
      {stock && <StockOptionsLink stock={stock} options={options} />}
    </div>
  );
}

// ─── OVERRIDE PANEL ───────────────────────────────────────────────────────────
function OverridePanel({ hasPosition, onClosePosition, onPauseBot }) {
  const [armClose, setArmClose] = useState(false);
  const [armPause, setArmPause] = useState(false);
  if (!hasPosition) return null;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "10px",
      padding: "8px 14px", background: C.s2,
      borderRadius: "8px", border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.red}`,
      marginTop: "10px",
    }}>
      <span style={{ fontSize: "9px", color: C.muted, fontFamily: "monospace", letterSpacing: "1px", textTransform: "uppercase" }}>Manual Override</span>
      <div style={{ width: "1px", height: "20px", background: C.border }} />

      {!armClose ? (
        <button onClick={() => setArmClose(true)} style={{ padding: "4px 12px", background: C.rDim, border: `1px solid ${C.red}40`, borderRadius: "4px", color: C.red, fontSize: "10px", fontWeight: 700, fontFamily: "monospace", cursor: "pointer" }}>
          ⏹ Close Position
        </button>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <span style={{ fontSize: "10px", color: C.red, fontFamily: "monospace" }}>Confirm?</span>
          <button onClick={() => { setArmClose(false); onClosePosition?.(); }} style={{ padding: "3px 8px", background: C.rDim, border: `1px solid ${C.red}`, borderRadius: "3px", color: C.red, fontSize: "10px", fontFamily: "monospace", cursor: "pointer" }}>YES</button>
          <button onClick={() => setArmClose(false)} style={{ padding: "3px 8px", background: C.s3, border: `1px solid ${C.border}`, borderRadius: "3px", color: C.muted, fontSize: "10px", fontFamily: "monospace", cursor: "pointer" }}>NO</button>
        </div>
      )}

      <div style={{ width: "1px", height: "20px", background: C.border }} />

      {!armPause ? (
        <button onClick={() => setArmPause(true)} style={{ padding: "4px 12px", background: C.aDim, border: `1px solid ${C.amber}40`, borderRadius: "4px", color: C.amber, fontSize: "10px", fontWeight: 700, fontFamily: "monospace", cursor: "pointer" }}>
          ⏸ Pause Bot
        </button>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <span style={{ fontSize: "10px", color: C.amber, fontFamily: "monospace" }}>Confirm?</span>
          <button onClick={() => { setArmPause(false); onPauseBot?.(); }} style={{ padding: "3px 8px", background: C.aDim, border: `1px solid ${C.amber}`, borderRadius: "3px", color: C.amber, fontSize: "10px", fontFamily: "monospace", cursor: "pointer" }}>YES</button>
          <button onClick={() => setArmPause(false)} style={{ padding: "3px 8px", background: C.s3, border: `1px solid ${C.border}`, borderRadius: "3px", color: C.muted, fontSize: "10px", fontFamily: "monospace", cursor: "pointer" }}>NO</button>
        </div>
      )}

      <div style={{ marginLeft: "auto", fontSize: "9px", color: C.subtle, fontFamily: "monospace" }}>
        Tap once to arm · Tap again to execute
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
/**
 * BotIntelligencePanel
 *
 * Insert in TradingCommand.js between MarketChart and EvaluationTimeline:
 *
 *   <BotIntelligencePanel
 *     liveBars={liveBars}
 *     activePositions={activePositions}
 *     closedPositions={closedPositions}
 *     currentPrice={currentPrice}
 *     metrics={metrics}
 *     onClosePosition={handleClosePosition}
 *   />
 */
export default function BotIntelligencePanel({
  liveBars         = [],
  activePositions  = [],
  closedPositions  = [],
  currentPrice     = 0,
  proceedEvals     = [],
  allOrders        = [],
  metrics          = {},
  onClosePosition,
  onPauseBot,
}) {
  const [viewMode, setViewMode] = useState("dashboard");

  const data = usePanelData({
    liveBars,
    activePositions,
    closedPositions,
    currentPrice,
    proceedEvals,
    metrics,
  });

  const hasPosition = data.panelState === "active";

  return (
    <div style={{ fontFamily: "'DM Mono', monospace", marginBottom: "12px" }}>
      <style>{FONTS}{`
        .bip-root * { box-sizing: border-box; color: unset; }
        .bip-root ::-webkit-scrollbar { width: 4px; height: 4px; }
        .bip-root ::-webkit-scrollbar-track { background: ${C.bg}; }
        .bip-root ::-webkit-scrollbar-thumb { background: ${C.b2}; border-radius: 2px; }
        .bip-btn { font-family: 'DM Mono', monospace; }
        @keyframes bipRipple {
          0%   { transform: translate(-50%,-50%) scale(1);   opacity: 0.7; }
          100% { transform: translate(-50%,-50%) scale(2.8); opacity: 0; }
        }
        @keyframes bipTrailFade {
          0%   { opacity: 0.55; }
          100% { opacity: 0; }
        }
        .bip-price-dot { transition: left 0.35s cubic-bezier(0.25,0.46,0.45,0.94); }
      `}</style>
      <div className="bip-root">

      {/* Panel header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "11px", fontWeight: 700, color: C.text, fontFamily: "'Syne', sans-serif", letterSpacing: "0.5px" }}>
            AGENT INTELLIGENCE PANEL
          </span>
          <div style={{ height: "1px", width: "40px", background: C.border }} />
          <span style={{ fontSize: "10px", color: C.muted, fontFamily: "monospace" }}>
            {viewMode === "dashboard" ? "Dashboard View" : "Analysis View"}
          </span>
          {/* State badge */}
          <span style={{
            fontSize: "9px", padding: "2px 7px",
            background: data.panelState === "active" ? C.gDim : data.panelState === "post_trade" ? C.cDim : C.bDim,
            color:      data.panelState === "active" ? C.green : data.panelState === "post_trade" ? C.cyan : C.blue,
            borderRadius: "3px", fontFamily: "monospace", fontWeight: 700,
          }}>
            {data.panelState.replace("_", " ").toUpperCase()}
          </span>
        </div>

        {/* View toggle */}
        <div style={{ display: "flex", gap: "2px", padding: "3px", background: C.s2, borderRadius: "6px", border: `1px solid ${C.border}` }}>
          {[["dashboard", "⬡ Dashboard"], ["analysis", "⊞ Analysis"]].map(([mode, label]) => (
            <button
              key={mode}
              className="bip-btn"
              onClick={() => setViewMode(mode)}
              style={{
                padding: "5px 14px",
                background:   viewMode === mode ? C.s3 : "transparent",
                border:       `1px solid ${viewMode === mode ? C.b2 : "transparent"}`,
                borderRadius: "4px",
                color:        viewMode === mode ? C.text : C.muted,
                fontSize:     "10px", cursor: "pointer",
                fontWeight:   viewMode === mode ? 700 : 400,
                transition:   "all 0.15s",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Main panel */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: "12px", padding: "16px" }}>
        {viewMode === "dashboard"
          ? <DashboardView data={data} />
          : <AnalysisView  data={data} />
        }
      </div>

      {/* Override panel (analysis view only, active position only) */}
      {viewMode === "analysis" && (
        <OverridePanel
          hasPosition={hasPosition}
          onClosePosition={onClosePosition}
          onPauseBot={onPauseBot}
        />
      )}

      {/* Footer */}
      <div style={{ marginTop: "8px", display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: "9px", color: C.subtle, fontFamily: "monospace" }}>BERBUL AI TRADING · Bot Intelligence Panel · v2.0</span>
        <span style={{ fontSize: "9px", color: C.subtle, fontFamily: "monospace" }}>bars every 1m · agent story on BUY/SELL signal · reactive check every 5m</span>
      </div>
      </div>{/* /bip-root */}
    </div>
  );
}